import { FC, JSX, useContext, useEffect, useState } from "react";
import { ZipProjectContext } from "./ZipProjectContext";
import SingleSelectDropdown, { DropdownItem } from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { ZipAnalysisSearchSelections, ZipProjectActionsEnum } from "./interfaces";
import { ProjectPerspective } from "../enumerators";
import { isZipCodeInputValid } from "@app/shared/utility";

export const ZipProjectFilters: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { state, dispatch } = projectContext;
  const [citySuggestions, setCitySuggestions] = useState<JSX.Element[]>([]);
  const [zipCodeSuggestions, setZipCodeSuggestions] = useState<JSX.Element[]>([]);

  const handleFilterChange = (selectedItem: DropdownItem<string> | string, filter: keyof ZipAnalysisSearchSelections) => {
    dispatch({
      payload: {
        filterSelections: {
          ...state.filterSelections,
          [filter]: selectedItem,
        }
      },
      type: ZipProjectActionsEnum.ChangeFilter,
    })
  };

  useEffect(() => {
    const newCitySuggestions = filterTextFieldSuggestions("city");
    setCitySuggestions(newCitySuggestions);
  }, [state.filterSelections.city, state.filterOptions.city]);

  useEffect(() => {
    const newZipCodeSuggestions = filterTextFieldSuggestions("zipCode");
    setZipCodeSuggestions(newZipCodeSuggestions);
  }, [state.filterSelections.zipCode, state.filterOptions.zipCode]);

  const filterTextFieldSuggestions = (key: "city" | "zipCode") => {
    if (!state.filterOptions[key]) {
      return;
    }

    const itemStartsWith: string[] = [];
    const itemIncludes: string[] = [];
    state.filterOptions[key].forEach((item) => {
      if (item.toLowerCase().startsWith(state.filterSelections[key].toLowerCase())) {
        itemStartsWith.push(item);
      } else if (item.toLowerCase().includes((state.filterSelections[key].toLowerCase()))) {
        itemIncludes.push(item);
      }
    });

    const newSuggestions = [...itemStartsWith, ...itemIncludes]
      .slice(0, 19)
      .map((o) => <option key={o} value={o} />);

    return newSuggestions;
  }

  return (
    <div className="card w-100">
      <div className="card-body">
        <div className="row justify-content-start">
          <SingleSelectDropdown
            label="Specialty"
            containerClassName="col"
            items={state.filterOptions.specialtyGroupCode}
            testId="zip-view-by-all__filter--specialty"
            onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "specialtyGroupCode")}
            selectedItem={state.filterSelections.specialtyGroupCode}
            disabled={state.loading}
          />
          <SingleSelectDropdown
            label="Classification"
            containerClassName="col"
            items={state.filterOptions.zipClass}
            testId="zip-view-by-all__filter--zip-class"
            onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "zipClass")}
            selectedItem={state.filterSelections.zipClass}
            disabled={state.loading}
          />

          {state.perspective === ProjectPerspective.ByZipCode && (
            <>
              <SingleSelectDropdown
                label="State"
                containerClassName="col"
                items={state.filterOptions.state}
                testId="zip-view-by-all__filter--state"
                onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "state")}
                selectedItem={state.filterSelections.state}
                disabled={state.loading}
              />
              <QESTextInput
                label="City"
                containerClassName="col"
                testId="zip-view-by-all__filter--city"
                onChange={(e) => {
                  handleFilterChange(e.target.value, "city");
                }}
                value={state.filterSelections.city}
                disabled={state.loading}
                list="cities"
              />
              <datalist id="cities">
                {citySuggestions}
              </datalist>
              <SingleSelectDropdown
                label="Primary County"
                containerClassName="col"
                items={state.filterOptions.county}
                testId="zip-view-by-all__filter--county"
                onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "county")}
                selectedItem={state.filterSelections.county}
                disabled={state.loading}
              />
              <QESTextInput
                label="Zip Code"
                containerClassName="col"
                testId="zip-view-by-all__filter--zip"
                onChange={(e) => {
                  if (!isZipCodeInputValid(e.target.value)) {
                    return;
                  }
                  handleFilterChange(e.target.value, "zipCode");
                }}
                value={state.filterSelections.zipCode}
                // should only allow digits, and should be a min and max length of 5
                // this doesn't prevent any user inputs, but could potentially be used for validation
                // see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState/patternMismatch
                pattern="^\d{5,5}$"
                disabled={state.loading}
                list="zip-codes"
              />
              <datalist id="zip-codes">
                {zipCodeSuggestions}
              </datalist>
            </>
          )}

          {state.perspective === ProjectPerspective.ByZipClass ? (
            <SingleSelectDropdown
              label="Met Overall"
              containerClassName="col"
              items={state.filterOptions.metOverall}
              testId="zip-view-by-all__filter--met-overall"
              onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "metOverall")}
              selectedItem={state.filterSelections.metOverall}
              disabled={state.loading}
            />
          ) : (
            <SingleSelectDropdown
              label="Met Overall"
              containerClassName="col"
              items={state.filterOptions.metPassPercent}
              testId="zip-view-by-all__filter--met-overall"
              onSelectionChanged={(selectedItem) => handleFilterChange(selectedItem, "metPassPercent")}
              selectedItem={state.filterSelections.metPassPercent}
              disabled={state.loading}
            />
          )}

          <QESButton
            className="px-2 align-self-end mb-2 py-0 mb-0"
            qesType="secondary"
            qesTestId="zip-view-by-all__filter-submit"
            onClick={projectContext.onApplyFilters}
            disabled={state.loading}
          >
            Apply
          </QESButton>
          <QESButton
            className="py-0 px-2 align-self-end mb-2 mr-2"
            qesType="secondary-danger"
            qesTestId="zip-view-by-all__filter-reset"
            onClick={projectContext.onResetFilters}
            disabled={state.loading}
          >
            Reset
          </QESButton>
        </div>
      </div>
    </div>
  )
}
